import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import {
  DISCLAIMER, investmentPlanService,
  type Recommendation, type RecStats, type StoredSignal,
} from '../services/investment-plan-service';
import { RecommendationList, RecStatsRow } from '../components/plan/RecommendationList';
import { useBaseCurrency } from '../stores/currency-store';

/**
 * Investments → Recommendations (issue #31): what the nightly check
 * recommends now, the decision history with follow-through, and the week's
 * watchlist. Recommendations only -- FolioWorth never places trades; whether
 * a recommendation was followed is inferred from imported trades.
 */
export const RecommendationsPage: React.FC = () => {
  useBaseCurrency();
  const [tab, setTab] = useState<'open' | 'history' | 'watchlist'>('open');
  const [open, setOpen] = useState<Recommendation[]>([]);
  const [history, setHistory] = useState<Recommendation[]>([]);
  const [signals, setSignals] = useState<StoredSignal[]>([]);
  const [stats, setStats] = useState<RecStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, h, s, w] = await Promise.all([
        investmentPlanService.recommendations('open'),
        investmentPlanService.recommendations('history'),
        investmentPlanService.stats(),
        investmentPlanService.signals(7),
      ]);
      setOpen(o.recommendations);
      setHistory(h.recommendations);
      setStats(s.stats);
      setSignals(w.signals);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const dismiss = async (rec: Recommendation, reason: string) => {
    await investmentPlanService.dismiss(rec.id, reason);
    await load();
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>Recommendations</Typography>
      <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
        From the nightly investment-plan check, gated by Jev. Follow-through is matched from your imported trades.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>{DISCLAIMER}</Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {stats && (
        <Card sx={{ mb: 2 }}><CardContent><RecStatsRow stats={stats} /></CardContent></Card>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="open" label={`Open (${open.length})`} />
        <Tab value="history" label={`History (${history.length})`} />
        <Tab value="watchlist" label={`Watchlist (${signals.length})`} />
      </Tabs>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : tab === 'open' ? (
        <RecommendationList recs={open} onDismiss={dismiss}
          empty="Nothing to act on. The plan is checked every night after the 03:00 sync." />
      ) : tab === 'history' ? (
        <RecommendationList recs={history} empty="No decisions yet." />
      ) : (
        <Box>
          {signals.length === 0 && (
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', py: 3 }}>No notable signals this week.</Typography>
          )}
          {signals.map((s) => (
            <Card key={s.symbol} sx={{ mb: 1.5 }}>
              <CardContent>
                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 700 }}>{s.symbol}</Typography>
                  <Chip size="small" label={s.choice.replace('_', ' ')} />
                  <Chip size="small" variant="outlined" label={`Jev ${parseFloat(s.confidence).toFixed(2)}`} />
                  {s.high_conviction && <Chip size="small" color="secondary" label="High conviction" />}
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'var(--text-secondary)' }}>
                  {s.reasons_md.replace(/\*\*/g, '')}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};
